import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Empty, Form, Input, Modal, Tooltip } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form/interface';
import { mergeWith } from 'lodash';

import { getModuleMenu } from '@/hooks/useMenu';
import { NAV_MAP } from '@/hooks/useMenu/constants';
import { getModule } from '@/hooks/useRouteScope';

import { ConfigContext } from '../ConfigProvider';
import { RenderIcon } from '../CustomAvatar';
import { DC_AUTH, GLOBAL_AUTH, SYSTEM_AUTH, TENANT_AUTH } from './constants';
import { expandRelativeOnCheck, normalizeRelativeFieldValues } from './editRoleUtils';
import styles from './index.module.less';

const FormItem = Form.Item;

/** 菜单树节点（与 getModuleMenu 返回结构一致，字段宽松以兼容历史数据） */
interface MenuTreeNode {
  title: string;
  icon?: React.ComponentType | string;
  hidden?: boolean;
  children?: MenuTreeNode[];
}

interface PermissionVerbRelative {
  onCheck?: Record<string, boolean>;
  onUnCheck?: Record<string, undefined>;
}

/** 单条动词权限配置 */
interface PermissionVerb {
  name: React.ReactNode;
  type: string;
  group?: string;
  resource?: string;
  /** 覆盖按接口资源和动词推断出的展示分组。 */
  category?: PermissionCategory;
  hidden?: boolean;
  /** 固定权限始终选中，不允许用户取消。 */
  disabled?: boolean;
  relative?: PermissionVerbRelative;
}

type PermissionCategory = 'view' | 'create' | 'edit' | 'delete' | 'other';

const PERMISSION_CATEGORIES: PermissionCategory[] = ['view', 'create', 'edit', 'delete', 'other'];

/** roleMap 中 list 内的一层（对应一个页面下的动词列表） */
interface PermissionListBlock {
  title?: string;
  resource?: string;
  group?: string;
  list: PermissionVerb[];
}

/** 侧边菜单对应的一块权限配置 */
interface RoleMapItem {
  title: string;
  titles?: string[];
  group?: string;
  resource?: string;
  list: PermissionListBlock[];
}

interface TransformResourceEntry {
  resource: string;
  verbs: string[];
}

interface TransformGroupEntry {
  group: string;
  resources: TransformResourceEntry[];
}

/** 组装角色权限提交参数；系统角色必须固定在非数据中心、非工作空间作用域。 */
function buildRolePermissionPayload(groups: TransformGroupEntry[], module: string) {
  return [
    {
      groups,
      namespace: '*',
      ...(module === 'system' ? { datacenter: '-', workspace: '-' } : {}),
    },
  ];
}

interface PermissionDependency {
  key: string;
  pageTitle: string;
  permissionName: React.ReactNode;
}

/** 将表单扁平值转为接口需要的 groups 结构 */
function transformData(data: Record<string, Record<string, Record<string, unknown>>>): TransformGroupEntry[] {
  const result: TransformGroupEntry[] = [];
  const groupKeys = Object.keys(data);

  for (const groupKey of groupKeys) {
    const currentGroup = data[groupKey];
    const resourceKeys = Object.keys(currentGroup);
    const groupsResult: Partial<TransformGroupEntry> & { resources?: TransformResourceEntry[] } = {};

    for (const resourceKey of resourceKeys) {
      const currentResource = currentGroup[resourceKey];
      const verbs = Object.keys(currentResource).filter((verb) => Boolean(currentResource[verb]));
      if (verbs.length === 0) {
        continue;
      }
      groupsResult.group = groupKey;
      if (!groupsResult.resources) {
        groupsResult.resources = [];
      }
      groupsResult.resources.push({
        resource: resourceKey,
        verbs,
      });
    }

    if (Object.keys(groupsResult).length > 0) {
      result.push(groupsResult as TransformGroupEntry);
    }
  }

  return result;
}

/** 取菜单树第一个叶子 key，以及其顶层 SubMenu 的 key（用于展开） */
function getKeysByData(data: MenuTreeNode[]): {
  key: string | undefined;
  openKey: string | undefined;
} {
  if (!data?.length) {
    return { key: undefined, openKey: undefined };
  }

  function findFirstLeaf(item: MenuTreeNode): { firstKey: string; openKey: string | undefined } {
    let firstKey = item.title;
    let openKey: string | undefined;
    if (item.children?.length) {
      const nested = findFirstLeaf(item.children[0]);
      openKey = item.title;
      firstKey = nested.firstKey || item.title;
    }
    return { firstKey, openKey };
  }

  const { firstKey, openKey } = findFirstLeaf(data[0]);
  return { key: firstKey, openKey };
}

export function parseItem(i: string): { group?: string; resource?: string; type?: string } {
  if (!i) {
    return {};
  }
  const match = i.match(/^\[["']?([^\]"']+)["']?\]\.([^.]+)\.([^.]+)$/);
  if (match) {
    return {
      group: match[1],
      resource: match[2],
      type: match[3],
    };
  }
  return {};
}

/** 编辑态：表单字段 path（与 defaultData、只读展示一致，group 用单引号） */
function fieldKeySingleQuote(group: string, resource: string, verbType: string): string {
  return `['${group}'].${resource}.${verbType}`;
}

function roleItemMatchesMenuKey(item: RoleMapItem, menuKey: string): boolean {
  return item.title === menuKey || (item.titles ?? []).includes(menuKey);
}

function findRoleConfig(roleMap: RoleMapItem[], menuKey: string): RoleMapItem | undefined {
  return roleMap.find((item) => roleItemMatchesMenuKey(item, menuKey));
}

/** 将自动勾选的接口依赖解析为用户可理解的页面和权限文案。 */
function getPermissionDependencies(roleMap: RoleMapItem[], dependencyKeys: string[]): PermissionDependency[] {
  return dependencyKeys.flatMap((key) => {
    const dependency = parseItem(key);
    for (const item of roleMap) {
      for (const block of item.list) {
        const verb = block.list.find(
          (candidate) =>
            (candidate.group || block.group || item.group) === dependency.group &&
            (candidate.resource || block.resource || item.resource) === dependency.resource &&
            candidate.type === dependency.type,
        );
        if (verb) {
          // 同一菜单下有多块（如标签定义 / 标签值）时，优先用 block.title 区分，避免都显示成「标签管理 / 列表」
          const pageTitle = block.title || NAV_MAP[item.title]?.title || item.title;
          return [
            {
              key,
              pageTitle: typeof pageTitle === 'string' ? pageTitle : NAV_MAP[item.title]?.title || item.title,
              permissionName: getBusinessPermissionName(verb.name),
            },
          ];
        }
      }
    }
    return [];
  });
}

/** 权限配置默认隐藏 list/get 等接口动词前缀，仅保留用户可理解的业务文案。 */
function getBusinessPermissionName(name: React.ReactNode): React.ReactNode {
  if (!React.isValidElement<{ children?: React.ReactNode }>(name)) {
    return name;
  }
  const children = React.Children.toArray(name.props.children);
  if (typeof children[0] !== 'string') {
    return name;
  }
  children[0] = children[0].replace(/^[a-zA-Z]+:\s*/, '');
  return React.cloneElement(name, undefined, children);
}

/** 仅统计用户可见勾选项；隐藏联动项不参与「仅显示已选」与已选数量展示 */
function isVisibleVerbSelected(form: FormInstance, verb: PermissionVerb, group: string, resource: string | undefined): boolean {
  if (verb.hidden) return false;
  return Boolean(form.getFieldValue(fieldKeySingleQuote(verb.group || group, verb.resource || resource || '', verb.type)));
}

function isRoleConfigSelected(form: FormInstance, config: RoleMapItem | undefined): boolean {
  return Boolean(
    config?.list.some((listBlock) => {
      const group = listBlock.group || config.group || '';
      const resource = listBlock.resource || config.resource || '';
      return listBlock.list.some((verb) => isVisibleVerbSelected(form, verb, group, resource));
    }),
  );
}

/** 按菜单文案和已选状态过滤树，同时保留命中叶子的祖先节点。 */
function filterPermissionMenu(
  data: MenuTreeNode[],
  keyword: string,
  selectedOnly: boolean,
  isSelected: (key: string) => boolean,
): MenuTreeNode[] {
  const normalizedKeyword = keyword.trim().toLocaleLowerCase();
  return data.flatMap((node) => {
    const label = NAV_MAP[node.title]?.title || node.title;
    const matchesKeyword = !normalizedKeyword || label.toLocaleLowerCase().includes(normalizedKeyword);
    const children = filterPermissionMenu(node.children || [], matchesKeyword ? '' : keyword, selectedOnly, isSelected);
    const matchesSelected = !selectedOnly || isSelected(node.title);
    if (children.length || (matchesKeyword && matchesSelected)) {
      return [{ ...node, children }];
    }
    return [];
  });
}

function menuContainsKey(data: MenuTreeNode[], key: string): boolean {
  return data.some((node) => node.title === key || menuContainsKey(node.children || [], key));
}

/** 查询优先按 verb 分类；带子资源路径的写动作保留为业务操作，避免把启用、发放等误称为创建。 */
function getPermissionCategory(verb: PermissionVerb): PermissionCategory {
  if (verb.category) return verb.category;
  if (verb.type === 'list' || verb.type === 'get') return 'view';
  if (verb.resource?.includes('/')) return 'other';
  if (verb.type === 'create') return 'create';
  if (verb.type === 'update' || verb.type === 'patch' || verb.type === 'put') return 'edit';
  if (verb.type === 'delete') return 'delete';
  return 'other';
}

interface PermissionModalProps {
  title?: string;
  show: boolean;
  roleMap: RoleMapItem[];
  defaultData?: Record<string, unknown>;
  onOk: (data: unknown[]) => void;
  onCancel: () => void;
}

export interface PermissionFormProps {
  form: FormInstance;
  roleMap: RoleMapItem[];
  defaultData?: Record<string, unknown>;
  module: string;
  show: boolean;
  readOnly?: boolean;
  style?: React.CSSProperties;
}

/** 权限表单：左侧菜单 + 右侧按页签展示动词勾选 */
export function PermissionForm({ form, roleMap, defaultData, module, show, readOnly = false, style }: PermissionFormProps) {
  const { t } = useTranslation();
  const [currKey, setCurrKey] = useState('');
  const [keyword, setKeyword] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [activeCenterKey, setActiveCenterKey] = useState('');
  const [formVersion, setFormVersion] = useState(0);
  const { backMenu } = useContext(ConfigContext);

  const pagesMenu = useMemo(() => {
    if (!show) {
      return [];
    }
    return getModuleMenu({ module, backMenu }) as MenuTreeNode[];
  }, [module, show, backMenu]);

  const selectedCount = useMemo(() => {
    // form 实例引用不变，用版本号触发程序化勾选后的重新统计。
    void formVersion;
    return roleMap.reduce(
      (total, item) =>
        total +
        item.list.reduce(
          (sum, block) =>
            sum +
            block.list.filter((verb) =>
              isVisibleVerbSelected(
                form,
                verb,
                verb.group || block.group || item.group || '',
                verb.resource || block.resource || item.resource,
              ),
            ).length,
          0,
        ),
      0,
    );
  }, [form, formVersion, roleMap]);

  const visiblePagesMenu = useMemo(() => {
    void formVersion;
    return filterPermissionMenu(pagesMenu, keyword, readOnly || selectedOnly, (key) =>
      isRoleConfigSelected(form, findRoleConfig(roleMap, key)),
    );
  }, [form, formVersion, keyword, pagesMenu, readOnly, roleMap, selectedOnly]);

  useEffect(() => {
    if (show && !menuContainsKey(visiblePagesMenu, currKey)) {
      const { key } = getKeysByData(visiblePagesMenu);
      setCurrKey(key ?? '');
    }
  }, [currKey, visiblePagesMenu, show]);

  useEffect(() => {
    // 不同中心可能复用同一个页面 key，优先保留用户当前选择的中心，避免命中前一个同名菜单后跳回。
    const selectedCenter = visiblePagesMenu.find((node) => node.title === activeCenterKey);
    const currentCenter =
      selectedCenter && menuContainsKey([selectedCenter], currKey)
        ? selectedCenter
        : visiblePagesMenu.find((node) => menuContainsKey([node], currKey));
    if (currentCenter && currentCenter.title !== activeCenterKey) {
      setActiveCenterKey(currentCenter.title);
    } else if (!visiblePagesMenu.some((node) => node.title === activeCenterKey)) {
      setActiveCenterKey(visiblePagesMenu[0]?.title || '');
    }
  }, [activeCenterKey, currKey, visiblePagesMenu]);

  useEffect(() => {
    if (show) {
      form.resetFields();
      setKeyword('');
      setSelectedOnly(false);
      setFormVersion((version) => version + 1);
    }
  }, [show, form]);

  useEffect(() => {
    form.resetFields();
    if (defaultData && Object.keys(defaultData).length > 0) {
      form.setFieldsValue(defaultData);
      setFormVersion((version) => version + 1);
    }
  }, [defaultData, form]);

  const renderVerbCollapse = useCallback(
    ({ listBlock, group, index, resource }: { listBlock: PermissionListBlock; group: string; index: number; resource?: string }) => {
      const categorizedPermissions = PERMISSION_CATEGORIES.map((category) => ({
        category,
        verbs: listBlock.list.filter((verb) => {
          if (verb.hidden || getPermissionCategory(verb) !== category) return false;
          if (!readOnly) return true;

          const dependencyKeys = Object.keys(verb.relative?.onCheck ?? {});
          const mainKey = fieldKeySingleQuote(verb.group || group, verb.resource || listBlock.resource || resource || '', verb.type);
          if (!defaultData?.[mainKey]) return false;
          return dependencyKeys.every((depKey) => {
            const { group: dependencyGroup, resource: dependencyResource, type } = parseItem(depKey);
            return Boolean(
              dependencyGroup &&
                dependencyResource &&
                type &&
                defaultData?.[fieldKeySingleQuote(dependencyGroup, dependencyResource, type)],
            );
          });
        }),
      })).filter(({ verbs }) => verbs.length > 0);

      if (readOnly && categorizedPermissions.length === 0) {
        return <Empty description={t('components:no.permission')} />;
      }

      const standardCategories = categorizedPermissions.filter(({ category }) => category !== 'other');
      const lastStandardCategory = standardCategories[standardCategories.length - 1]?.category;

      // 本块已注册 FormItem 的 field（含隐藏项），勿再挂共享依赖 FormItem，避免同 field 重复注册
      const ownedFieldKeys = new Set(
        listBlock.list.map((verb) =>
          fieldKeySingleQuote(verb.group || group, verb.resource || listBlock.resource || resource || '', verb.type),
        ),
      );
      // 同一 listBlock 内多个 verb 可能共享 onCheck 依赖，隐藏 FormItem 只渲染一次
      const sharedDependencyKeys = Array.from(
        new Set(listBlock.list.flatMap((verb) => (verb.hidden ? [] : Object.keys(verb.relative?.onCheck ?? {})))),
      ).filter((dep) => {
        const { group: depGroup, resource: depResource, type: depType } = parseItem(dep);
        if (!depGroup || !depResource || !depType) return true;
        // onCheck key 可能是双引号，需归一成与 FormItem 一致的单引号 path 再比较
        return !ownedFieldKeys.has(fieldKeySingleQuote(depGroup, depResource, depType));
      });

      return (
        <div className={styles.permissionList} key={index}>
          {categorizedPermissions.map(({ category, verbs }) => {
            const fields = verbs.map((verb) =>
              fieldKeySingleQuote(verb.group || group, verb.resource || listBlock.resource || resource || '', verb.type),
            );
            const selectedFields = fields.filter((field) => Boolean(form.getFieldValue(field)));
            const fillsRow = category === 'other' || (standardCategories.length % 2 === 1 && category === lastStandardCategory);

            const handleCategoryChecked = (checked: boolean) => {
              const values: Record<string, boolean | undefined> = {};
              verbs.forEach((verb, verbIndex) => {
                values[fields[verbIndex]] = verb.disabled || checked || undefined;
                Object.assign(
                  values,
                  checked ? expandRelativeOnCheck(roleMap, verb.relative?.onCheck) : normalizeRelativeFieldValues(verb.relative?.onUnCheck),
                );
              });
              form.setFieldsValue(values);
              setFormVersion((version) => version + 1);
            };

            return (
              <section className={`${styles.permissionGroup} ${fillsRow ? styles.permissionGroupWide : ''}`} key={category}>
                <div className={styles.permissionGroupTitle}>
                  <span>{t(`components:permission.group.${category}`)}</span>
                  {!readOnly && (
                    <Checkbox
                      checked={selectedFields.length === fields.length}
                      indeterminate={selectedFields.length > 0 && selectedFields.length < fields.length}
                      onChange={handleCategoryChecked}
                    >
                      {t('components:select.all')}
                    </Checkbox>
                  )}
                </div>
                <div className={styles.permissionGroupItems}>
                  {verbs.map((verb, verbIndex) => {
                    const dependencyKeys = Object.keys(verb.relative?.onCheck ?? {});
                    const dependencies = getPermissionDependencies(roleMap, dependencyKeys);
                    const itemKey = `${fields[verbIndex]}#${verbIndex}`;
                    if (readOnly) {
                      return (
                        <div className={styles.permissionItem} key={itemKey}>
                          <Checkbox checked disabled>
                            {getBusinessPermissionName(verb.name)}
                          </Checkbox>
                          {dependencies.length > 0 && (
                            <div className={styles.permissionDependencies}>
                              {t('components:permission.dependencies')}：
                              {dependencies.map(({ key, pageTitle, permissionName }, dependencyIndex) => (
                                <React.Fragment key={`${key}#${dependencyIndex}`}>
                                  {dependencyIndex > 0 && '、'}
                                  {pageTitle} / {permissionName}
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div className={styles.permissionItem} key={itemKey}>
                        <FormItem field={fields[verbIndex]} initialValue={verb.disabled || undefined}>
                          <PermissionVerbCheckbox
                            verb={{ ...verb, name: getBusinessPermissionName(verb.name) }}
                            relative={verb.relative}
                            form={form}
                            roleMap={roleMap}
                          />
                        </FormItem>
                        {dependencies.length > 0 && (
                          <div className={styles.permissionDependencies}>
                            {t('components:permission.dependencies')}：
                            {dependencies.map(({ key, pageTitle, permissionName }, dependencyIndex) => (
                              <React.Fragment key={`${key}#${dependencyIndex}`}>
                                {dependencyIndex > 0 && '、'}
                                {pageTitle} / {permissionName}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
          {!readOnly &&
            sharedDependencyKeys.map((dep) => {
              const { group: depGroup, resource: depResource, type: depType } = parseItem(dep);
              const field = depGroup && depResource && depType ? fieldKeySingleQuote(depGroup, depResource, depType) : dep;
              return (
                <FormItem key={field} field={field} hidden>
                  <PermissionVerbCheckbox verb={{ name: dep } as PermissionVerb} form={form} />
                </FormItem>
              );
            })}
          {!readOnly &&
            listBlock.list
              .filter((verb) => verb.hidden)
              .map((verb, hiddenIndex) => (
                <FormItem
                  key={`${verb.resource || listBlock.resource || resource}-${verb.type}#${hiddenIndex}`}
                  field={fieldKeySingleQuote(verb.group || group, verb.resource || listBlock.resource || resource || '', verb.type)}
                  hidden
                >
                  <PermissionVerbCheckbox verb={verb} relative={verb.relative} form={form} />
                </FormItem>
              ))}
        </div>
      );
    },
    [defaultData, form, readOnly, roleMap, t],
  );

  const noAuthConfig = useMemo(() => {
    const curr = findRoleConfig(roleMap, currKey);
    return !curr?.list?.length;
  }, [roleMap, currKey]);

  const activeCenter = visiblePagesMenu.find((node) => node.title === activeCenterKey) || visiblePagesMenu[0];
  const showFunctionNav = !activeCenter || Boolean(activeCenter.children?.length);

  const renderMenuTree = (menuNodes: MenuTreeNode[]) =>
    menuNodes.map((node) => {
      const { children, title } = node;
      if (!children?.length) {
        const label = NAV_MAP[title]?.title || title;
        return (
          <button
            type="button"
            key={title}
            className={currKey === title ? styles.menuItemActive : styles.menuItem}
            onClick={() => setCurrKey(title)}
          >
            <RenderIcon size={16} Icon={node.icon} style={{ marginRight: 8 }} />
            <Tooltip content={label} position="right">
              <span className={styles.menuLabel}>{label}</span>
            </Tooltip>
          </button>
        );
      }

      return (
        <div className={styles.menuGroup} key={title}>
          <div className={styles.menuGroupTitle}>{NAV_MAP[title]?.title || title}</div>
          {renderMenuTree(children)}
        </div>
      );
    });

  return (
    <Form
      form={form}
      autoComplete="off"
      layout="vertical"
      className={styles.form}
      style={style}
      onValuesChange={() => setFormVersion((version) => version + 1)}
    >
      <div className={styles.topToolbar}>
        <Input.Search className={styles.globalSearch} value={keyword} placeholder={t('common:search')} allowClear onChange={setKeyword} />
        {!readOnly && (
          <div className={styles.permissionSummary}>
            <Checkbox checked={selectedOnly} onChange={setSelectedOnly}>
              {t('components:show.selected.permissions.only')}
            </Checkbox>
            <span>{t('components:selected.permissions.count', { count: selectedCount })}</span>
          </div>
        )}
      </div>
      <div className={styles.workspace}>
        <div className={`${styles.menu_c} ${showFunctionNav ? '' : styles.menuWithoutFunctionNav}`}>
          <div className={styles.centerNav}>
            {visiblePagesMenu.map((center) => {
              const label = NAV_MAP[center.title]?.title || center.title;
              const active = center.title === activeCenter?.title;
              return (
                <Tooltip key={center.title} content={label} position="right">
                  <button
                    type="button"
                    className={active ? styles.centerNavItemActive : styles.centerNavItem}
                    onClick={() => {
                      setActiveCenterKey(center.title);
                      const { key } = getKeysByData(center.children?.length ? center.children : [center]);
                      setCurrKey(key || center.title);
                    }}
                  >
                    <RenderIcon size={18} Icon={center.icon} style={{ marginRight: 0 }} />
                    <span className={styles.centerNavLabel}>{label}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
          {showFunctionNav && (
            <div className={styles.functionNav}>
              {activeCenter && <div className={styles.currentCenter}>{NAV_MAP[activeCenter.title]?.title || activeCenter.title}</div>}
              {activeCenter?.children?.length ? <div className={styles.menu}>{renderMenuTree(activeCenter.children)}</div> : null}
              {!visiblePagesMenu.length && <Empty className={styles.menuEmpty} description={t('components:no.matching.result')} />}
            </div>
          )}
        </div>
        <div className={styles.menu_content_c}>
          <div className={styles.menu_content}>
            {roleMap.map((item) => (
              <FormItem key={item.title} hidden={!roleItemMatchesMenuKey(item, currKey)}>
                {item.list.map((listBlock, index) => {
                  const pageKey = index === 0 ? currKey : listBlock.title || currKey;
                  const blockSelectedCount = listBlock.list.filter((verb) =>
                    isVisibleVerbSelected(
                      form,
                      verb,
                      verb.group || listBlock.group || item.group || '',
                      verb.resource || listBlock.resource || item.resource,
                    ),
                  ).length;
                  return (
                    <div key={index} className={styles.listItem}>
                      <div className={styles.pageHeader}>
                        <div>
                          <div className={styles.listItemTitle}>{NAV_MAP[pageKey]?.title || pageKey}</div>
                        </div>
                        <div className={styles.pageCount}>
                          {t('components:permission.selection.progress', {
                            selected: blockSelectedCount,
                            total: listBlock.list.filter((verb) => !verb.hidden).length,
                          })}
                        </div>
                      </div>
                      {renderVerbCollapse({
                        listBlock,
                        index,
                        group: listBlock.group || item.group || '',
                        resource: listBlock.resource || item.resource,
                      })}
                    </div>
                  );
                })}
              </FormItem>
            ))}
            {noAuthConfig && <Empty style={{ marginTop: 100 }} description={t('components:no.permission.configuration.required')} />}
          </div>
        </div>
      </div>
    </Form>
  );
}

/** 主弹窗：收集勾选结果并回调 */
export function PermissionModal({ title, roleMap, show, defaultData, onCancel, onOk }: PermissionModalProps) {
  const module = getModule();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setVisible(show);
  }, [show]);

  const handleOk = async () => {
    try {
      const values = (await form.validate()) as Record<string, Record<string, Record<string, unknown>>>;

      let merged: Record<string, Record<string, Record<string, unknown>>>;
      if (module === 'console') {
        merged = mergeWith(values, GLOBAL_AUTH, TENANT_AUTH) as typeof values;
      } else if (module === 'data-center') {
        merged = mergeWith(values, GLOBAL_AUTH, DC_AUTH) as typeof values;
      } else if (module === 'system') {
        merged = mergeWith(values, GLOBAL_AUTH, SYSTEM_AUTH) as typeof values;
      } else {
        merged = mergeWith(values, GLOBAL_AUTH) as typeof values;
      }

      const groups = transformData(merged);
      setLoading(true);
      onOk(buildRolePermissionPayload(groups, module));
      handleCancel();
    } catch {
      // 校验未通过，不打断用户
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      style={{ width: '94vw', minWidth: 690, maxWidth: 1600 }}
      title={title || t('components:permission.configuration')}
      confirmLoading={loading}
      className={styles.edModal}
      onOk={handleOk}
      onCancel={handleCancel}
      autoFocus={false}
      focusLock={false}
      maskClosable={false}
    >
      <PermissionForm form={form} roleMap={roleMap} defaultData={defaultData} module={module} show={show} />
    </Modal>
  );
}

interface PermissionVerbCheckboxProps {
  verb: PermissionVerb;
  value?: boolean;
  onChange?: (v: boolean | undefined) => void;
  relative?: PermissionVerbRelative;
  form: FormInstance;
  roleMap?: RoleMapItem[];
}

/** 单权限勾选：处理 relative.onCheck / onUnCheck 联动（onCheck 递归展开） */
function PermissionVerbCheckbox({ onChange, verb, value, relative = {}, form, roleMap = [] }: PermissionVerbCheckboxProps) {
  const handleChecked = (checked: boolean) => {
    const { onCheck, onUnCheck } = relative;

    if (!checked) {
      onChange?.(undefined);
      if (onUnCheck) {
        form.setFieldsValue(normalizeRelativeFieldValues(onUnCheck));
      }
      return;
    }
    if (onCheck) {
      form.setFieldsValue(expandRelativeOnCheck(roleMap, onCheck));
    }
    onChange?.(checked);
  };

  return (
    <Checkbox onChange={handleChecked} checked={verb.disabled || Boolean(value)} disabled={verb.disabled}>
      {verb.name}
    </Checkbox>
  );
}
